// Client-epoch gate: get stale JS bundles off the clinic's devices BEFORE a
// database change that would make them misbehave.
//
// WHY: a tab keeps running the bundle it loaded until someone reloads it, and
// clinic iPads keep tabs open for days (sessions slide with the heartbeat).
// VersionChecker only shows a dismissible toast. When a deploy changes what
// the database lets the browser read, an old tab does not just look wrong, it
// can WRITE wrong data: before sb_jwt Phase 1, the old data-context built
// tickets from realtime payloads, and once P1 trims those payloads to
// (id, clinic_id, status, deleted, patient_id, closed_at), an old /tickets/cobrar
// tab seeds the register with discount 0 / total 0 and PATCHes that back.
//
// HOW: every bundle from this one on sends CLIENT_EPOCH_HEADER on
// /api/heartbeat (every 60 s) and /api/data (boot + refresh). The server
// compares it with its own CLIENT_EPOCH:
//   · header ABSENT  → 'pre-gate': a bundle older than this gate. The only
//     lever such a bundle obeys is a 401 on those two routes (session-client
//     kickToLogin): the tab goes to the login page and loads the new bundle.
//     It costs that device one re-login.
//   · header < epoch → 'stale': a gated bundle from an older epoch. Answered
//     with 409 STALE_CLIENT_CODE; the client reloads itself (no logout,
//     sessionStorage loop guard in session-client.reloadStaleClient).
//   · otherwise      → 'ok'.
//
// BUMP CLIENT_EPOCH (and only then) in the same PR as a change that breaks
// tabs still running the previous bundle — a column-scoping migration, a
// removed field the old bundle writes back, a renamed route. The bump reloads
// every open tab within ~60 s of the deploy, so deploy such a PR off-hours.

export const CLIENT_EPOCH = 1;
export const CLIENT_EPOCH_HEADER = 'x-rehaut-client-epoch';
export const STALE_CLIENT_STATUS = 409;
export const STALE_CLIENT_CODE = 'stale_client';

export type ClientEpochVerdict = 'ok' | 'pre-gate' | 'stale';

export function clientEpochVerdict(header: string | null | undefined, serverEpoch: number = CLIENT_EPOCH): ClientEpochVerdict {
  if (header == null || header.trim() === '') return 'pre-gate';
  const n = Number(header);
  // Garbage is treated as stale (reload), never as pre-gate (logout).
  if (!Number.isFinite(n)) return 'stale';
  return n < serverEpoch ? 'stale' : 'ok';
}

/** Headers a gated bundle attaches to /api/heartbeat and /api/data. */
export function clientEpochHeaders(): Record<string, string> {
  return { [CLIENT_EPOCH_HEADER]: String(CLIENT_EPOCH) };
}
